<template>
  <div class="w-full overflow-x-hidden">
    <page-loader v-if="loading" message="Loading Job details..." />
    <Page v-else>
      <div class="page">
        <JobHeader @refresh-job="refreshJob" />
        <TabSwitcher id="jp" :tabs="tabs" @tab-change="onTabChange" />
        <ImportedDataWarning />
        <JobStatusFinalWarning />
      </div>
    </Page>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable";
import { EventBus } from "@/lib/event_bus.js";
import { getProperty } from "@/lib/helpers.js";
import { getPathValueAt, updateQueryParam } from "@/lib/url.js";
import { useAccessRole } from "@/lib/useAccessRole";

import ImportedDataWarning from "@/components/ImportedDataWarning.vue";
import JobStatusFinalWarning from "@/components/JobStatusFinalWarning.vue";
import Page from "@/components/Page.vue";
import PageLoader from "@/components/PageLoader.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import VueChannel from "@/channels/VueChannel";
import JobHeader from "./Header/JobHeader.vue";
import AppointmentTab from "./Tabs/AppointmentTab.vue";
import CustomFormsTab from "./Tabs/CustomFormsTab.vue";
import InvoiceTab from "./Tabs/InvoiceTab.vue";
import JobCallsTab from "./Tabs/JobCallsTab.vue";
import JobTimesTab from "./Tabs/JobTimesTab.vue";
import MediaTab from "./Tabs/MediaTab.vue";
import PurchaseOrdersTab from "./Tabs/PurchaseOrdersTab.vue";
import QuotesTab from "./Tabs/QuotesTab.vue";

const store = useStore();
const { canAccess } = useAccessRole();

// Reactive state
const phoneFilters = ref<string[]>([]);
const loading = ref(true);
let channel: VueChannel | null = null;

// Store state
const tenant = computed(() => store.state.tenant);
const fetchingTenant = computed(() => store.state.fetchingTenant);
const job = computed(() => store.state.job);

// This getter coerces a missing flag key to false; useFeatureFlag does not, and
// TabSwitcher hides a tab only on `visible === false`, so an undefined there
// would show the tab with the flag off.
const customFormsEnabled = computed(() =>
  store.getters.featureEnabledForTenant("custom_forms_ff_enabled")
);

// Tabs configuration
const tabs = computed(() => [
  { name: "Appointments", component: AppointmentTab },
  {
    name: "Quotes",
    ref: "quotesTab",
    component: QuotesTab,
    beforeTabChange: (tabRef: { setCurrentQuote: (val: null) => void }) => {
      updateQueryParam("quote_id", null);
      tabRef?.setCurrentQuote(null);
      return true;
    },
    visible: canAccess("quoting")
  },
  {
    name: "Invoice",
    component: InvoiceTab,
    visible: canAccess("financial_data")
  },
  {
    // `name` is an identifier, not a label: it is the `tab=jp_<name>` token in
    // every deep link, the `data-cy` hook, and the wrapper CSS class. Renaming it
    // silently dead-links every URL already built against the old token, so the
    // user-facing rename rides on displayName instead.
    name: "Forms",
    displayName: "Forms & Checklists",
    ref: "customFormsTab",
    component: CustomFormsTab,
    beforeTabChange: (tabRef: {
      setCurrentSubmission: (val: null) => void;
    }) => {
      updateQueryParam("custom_form_submission_id", null);
      tabRef?.setCurrentSubmission(null);
      return true;
    },
    // Every endpoint behind this tab is authorized inside the `jobs` permission
    // group, so the flag alone would show the tab to an admin who then 403s.
    visible: customFormsEnabled.value && canAccess("jobs")
  },
  { name: "Media and Documents", component: MediaTab },
  { name: "Purchase Orders", component: PurchaseOrdersTab },
  { name: "Job Times", component: JobTimesTab },
  {
    name: "Phone Calls",
    component: JobCallsTab,
    props: {
      filters: phoneFilters.value
    }
  }
]);

// Store actions
function fetchJob(params: { jobId: string | number }) {
  return store.dispatch("fetchJob", params);
}

// Methods
async function fetchPhoneCallFilters() {
  const data = await fetch(`/jobs/initial_page_data`, {
    method: "GET"
  }).then((res) => res.json());
  phoneFilters.value = data.filters;
}

function refreshJob() {
  if (job.value?.id) {
    fetchJob({ jobId: job.value.id });
  }
}

function handleAppointmentUpdated(data: { id: number }) {
  const ids = (job.value?.appointments ?? []).map((a: { id: number }) =>
    String(a.id)
  );
  if (ids.includes(String(data.id))) {
    refreshJob();
  }
}

function onTabChange(tabName: string) {
  if (tabName !== "Appointments") {
    updateQueryParam("appointment_id", null);
  }
  if (tabName !== "Quotes") {
    updateQueryParam("quote_id", null);
  }
  if (tabName !== "Forms") {
    updateQueryParam("custom_form_submission_id", null);
  }
}

// Watch for job changes
watch(job, (newValue, oldValue) => {
  if (
    getProperty(newValue, "customer.id") !==
    getProperty(oldValue, "customer.id")
  ) {
    store.dispatch("setCurrentUser", newValue.customer);
    store.dispatch("addBreadcrumb", { label: `Job #${newValue.id}` });
  }
});

// Lifecycle
onMounted(async () => {
  // Fire-and-forget: the job fetch below must not wait on a stylesheet.
  import("./jobs_tailwind.css");

  EventBus.$on("refresh-job", refreshJob);
  EventBus.$on("appointment-updated", handleAppointmentUpdated);

  try {
    const jobId = getPathValueAt(1);
    await Promise.all([
      fetchJob({ jobId }),
      store.dispatch("fetchTenantCustomizations"),
      fetchPhoneCallFilters()
    ]);
    if (!tenant.value && !fetchingTenant.value) {
      await store.dispatch("fetchTenant");
    }
  } catch (e) {
    console.log(e);
  }
  loading.value = false;

  // Subscribe to cable channel after page is loaded (non-blocking)
  channel = new VueChannel();
  cable.subscribe(channel).catch((err) => {
    console.log("Unable to subscribe to VueChannel", err);
  });
});

onBeforeUnmount(() => {
  EventBus.$off("refresh-job", refreshJob);
  EventBus.$off("appointment-updated", handleAppointmentUpdated);
  channel?.disconnect();
  store.dispatch("clearCurrentUser");
  store.dispatch("clearJob");
});
</script>

<style scoped>
.page > div {
  margin-bottom: 1rem;
}
</style>
